import {
  ActionIcon,
  Avatar,
  Box,
  Center,
  Group,
  LoadingOverlay,
  Button as MantineButton,
  Popover,
  rem,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSyncDetailDrawer } from '../../hooks/useSyncDetailDrawer';
import { Button, IconButton, LoadingIndicator } from '@vrplatform/components';
import {
  type RequestQuery,
  useApiQuery,
  useDashboard,
  useTeam,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import { fromNow } from '@vrplatform/utils';
import { useNavigate } from 'react-router';

type SyncsApiQuery = RequestQuery<'get:/syncs'>;

type SyncRow = {
  id: string;
  title: string;
  createdAt: string;
  extractConnection: {
    icon: string | null;
    name: string | null;
  };
};

function useActiveSyncsQuery() {
  const [dashboard] = useDashboard();
  const [{ isGL: GL, id: teamId }] = useTeam();

  const skip = !teamId || dashboard !== 'propertyManager';

  return useApiQuery(
    async (api, signal) => {
      const getSyncs = (status: NonNullable<SyncsApiQuery['status']>) =>
        api.GET('/syncs', {
          params: {
            query: {
              limit: 100,
              sort: '-updated',
              status,
            } satisfies SyncsApiQuery,
          },
          signal,
        });

      const [startedResult, queuedResult] = await Promise.all([
        getSyncs('started'),
        getSyncs('queued'),
      ]);

      if (startedResult.error) throw startedResult.error;
      if (queuedResult.error) throw queuedResult.error;

      return [...startedResult.data.data, ...queuedResult.data.data]
        .sort(
          (left, right) =>
            Date.parse(right.updatedAt) - Date.parse(left.updatedAt)
        )
        .map(
          (sync): SyncRow => ({
            id: sync.id,
            title: sync.message ?? '',
            createdAt: sync.createdAt,
            extractConnection: {
              icon: sync.connection.icon ?? null,
              name: sync.connection.name ?? sync.connection.appName ?? null,
            },
          })
        );
    },
    {
      skip,
      keepPreviousData: true,
      queryGroup: 'connections',
      uniqueQueryKey: 'navbar-active-syncs',
      variables: {
        teamId,
        dashboard,
        GL,
      },
    }
  );
}

export const NavbarSyncsIconButton = () => {
  const [opened, handlers] = useDisclosure(false);

  const queryData = useActiveSyncsQuery();

  const goto = useNavigate();
  const gotoSyncs = () => goto('/connections/syncs');

  const hasPendingSync = !!queryData.data?.length;

  if (!hasPendingSync) return null;

  return (
    <Popover
      position="bottom-start"
      opened={opened}
      onChange={handlers.toggle}
      shadow="lg"
      withinPortal
      radius={'md'}
      width={380}
    >
      <Popover.Target>
        <ActionIcon variant="transparent" onMouseEnter={handlers.open}>
          <LoadingIndicator size={'0.8rem'} color="white" />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown
        p={0}
        sx={{
          // overflow: 'hidden',
          height: 500,
          maxHeight: '80dvh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={(theme) => ({
            borderBottom: `1px solid ${theme.colors.gray[2]}`,
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: theme.spacing.sm,
            paddingRight: theme.spacing.xs,
            justifyContent: 'space-between',
          })}
        >
          <Text fw={500}>Active syncs</Text>
          <IconButton
            icon="CrossIcon"
            iconSize={18}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              handlers.close();
            }}
          />
        </Box>

        <Content closePopover={handlers.close} queryData={queryData} />

        <MantineButton
          variant="white"
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            gotoSyncs();
            handlers.close();
          }}
          sx={(theme) => ({
            display: 'block',
            minHeight: 40,
            height: 'auto',
            border: 'none',
            borderTop: `1px solid ${theme.colors.gray[2]}`,
            paddingTop: rem(10),
            paddingBottom: rem(10),
            marginTop: 'auto',
            width: '100%',
            textAlign: 'center',
            color: theme.colors[theme.primaryColor][6],
            fontWeight: 'normal',
            borderTopRightRadius: 0,
            borderTopLeftRadius: 0,
          })}
        >
          Go to Syncs
        </MantineButton>
      </Popover.Dropdown>
    </Popover>
  );
};

const Content = ({
  closePopover,
  queryData,
}: {
  closePopover: () => void;
  queryData: ReturnType<typeof useActiveSyncsQuery>;
}) => {
  const { error, refetch, data, isInitialLoading: loading } = queryData;
  const syncs = data ?? [];

  if (loading && !syncs.length) {
    return (
      <Center flex={1} h="100%">
        <LoadingIndicator size="sm" />
      </Center>
    );
  }

  if (error) {
    console.error(error);
    return (
      <Center
        flex={1}
        h="100%"
        sx={{
          flexDirection: 'column',
        }}
      >
        <Icon
          icon="AlertTriangleIcon"
          size={28}
          color={(theme) => theme.colors.red[6]}
        />
        <Text ta="center" my="md" c="red">
          Failed to load syncs
        </Text>
        <Button
          onClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            refetch();
          }}
          leftIcon={'RefreshCwIcon'}
          variant="light"
          size="xs"
        >
          Try again
        </Button>
      </Center>
    );
  }

  return (
    <ScrollArea type="always" scrollbars="y" pos="relative">
      <List syncs={syncs} closePopover={closePopover} />

      <LoadingOverlay visible={loading} />
    </ScrollArea>
  );
};

const List = ({
  syncs,
  closePopover,
}: {
  syncs: SyncRow[];
  closePopover: () => void;
}) => {
  if (!syncs.length)
    return (
      <Center
        flex={1}
        h="100%"
        mih={250}
        maw={250}
        mx="auto"
        sx={{
          flexDirection: 'column',
        }}
      >
        <Icon
          icon="NotificationIcon"
          size={28}
          color={(theme) => theme.colors.neutral[6]}
        />
        <Text ta="center" mt="md" mb="xs" c="neutral" fw={500}>
          You're all caught up
        </Text>
        <Text ta="center" mb="md" c="gray">
          You'll be notified here for issues needed your attention.
        </Text>
      </Center>
    );

  return (
    <Stack gap={0}>
      {syncs.map((sync) => {
        if (!sync?.id) return null;

        return <SyncItem {...sync} key={sync.id} closePopover={closePopover} />;
      })}
    </Stack>
  );
};

const SyncItem = ({
  closePopover,
  ...sync
}: SyncRow & { closePopover: () => void }) => {
  const { open: openSyncDetailDrawer } = useSyncDetailDrawer();

  return (
    <Group
      wrap="nowrap"
      gap="xs"
      p="sm"
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        closePopover();
        openSyncDetailDrawer(sync.id);
      }}
      sx={(theme) => ({
        borderBottom: `1px solid ${theme.colors.gray[2]}`,
        cursor: 'pointer',
        ':hover': {
          backgroundColor: theme.colors.neutral[0],
        },
      })}
    >
      <Avatar
        src={sync.extractConnection.icon}
        size="sm"
        styles={{
          placeholder: { visibility: 'hidden' },
        }}
        sx={(theme) => ({
          border: `1px solid ${theme.colors.gray[2]}`,
        })}
      />
      <Box flex={1}>
        <Group justify="space-between" w="100%" wrap="nowrap">
          <Text size="xs" c="gray" m={0}>
            {sync.extractConnection.name}
          </Text>

          <Text size="xs" c="gray" m={0} ta="right">
            {fromNow(sync.createdAt)}
          </Text>
        </Group>
        <Text>{sync.title}</Text>
      </Box>
    </Group>
  );
};
